//dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//service contracts
import {
	EmployeeCustomFieldUpdate,
	EmployeeCustomFieldUpdateJSON,
	GetEmployeeDetailResponse,
	GetEmployeeDetailsHistoryResponse,
	GetEmployeeDetailsResponseJSON,
} from "@performyard/service-contracts";

import { employeeCustomFieldsQuery } from "../../../api/queries/defs/custom-fields";
import {
	deleteEmployeeAvatar,
	deleteEmployeeCustomFieldHistory,
	editEmployeeActivation,
	getEmployeeCustomField,
	getEmployeeCustomFieldHistory,
	patchEmployeeBulkCustomFields,
	patchEmployeeCustomFields,
	postEmployeeAvatar,
} from "../../../api/services/defs/internal-api";

import { cropImageToCenterRange } from "../../../utils/CropImage";
import refetchCachedImages from "../../../utils/refetchCachedImages";

import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useEmployeeCustomFields = (
	employeeId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailsResponseJSON>
) => {
	return useQuery<GetEmployeeDetailsResponseJSON>(employeeCustomFieldsQuery(employeeId, options));
};

export const useEmployeeCustomField = (
	employeeId: string,
	fieldId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailResponse>
) => {
	return useQuery<GetEmployeeDetailResponse>({
		queryKey: ["employeeCustomFields", employeeId, fieldId],
		queryFn: async () => await getEmployeeCustomField(employeeId, fieldId),
		meta: {
			errorMessage: "There was a problem fetching the employee custom field",
		},
		...options,
	});
};

export const useEmployeeCustomFieldHistory = (
	employeeId: string,
	fieldId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailsHistoryResponse>
) => {
	return useQuery<GetEmployeeDetailsHistoryResponse>({
		queryKey: ["employeeCustomFieldsHistory", employeeId, fieldId],
		queryFn: async () => await getEmployeeCustomFieldHistory(employeeId, fieldId),
		meta: {
			errorMessage: "There was a problem fetching the employee custom field history",
		},
		...options,
	});
};

export const useMutateEmployeeCustomFields = (onSuccess?: () => void, options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldPatch"],
		mutationFn: async ({
			updates,
			asOf,
			employeeId,
		}: {
			updates: EmployeeCustomFieldUpdate[];
			asOf: Date;
			employeeId: string;
		}) => await patchEmployeeCustomFields(updates, employeeId, asOf),
		...options,
		onSuccess: (data, variables) => {
			onSuccess && onSuccess();
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["emailTokens", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
		},
		meta: {
			errorMessage: "There was a problem updating the employee custom field",
		},
	});
};

export const useMutateBulkEmployeeCustomFields = (options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldPatch"],
		mutationFn: async (
			updatesList: {
				updates: EmployeeCustomFieldUpdateJSON[];
				asOf: Date;
				employeeId: string;
			}[]
		) => await patchEmployeeBulkCustomFields(updatesList),
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options?.onSuccess(data, variables, context);
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields"] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory"] });
			queryCache.invalidateQueries({ queryKey: ["InternalApiUser"] });
		},
		meta: {
			errorMessage: "There was a problem updating the employee custom field",
		},
	});
};

export const useDeleteEmployeeCustomFieldHistory = (employeeId: string) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldHistoryDelete"],
		mutationFn: async (historyId: string) => await deleteEmployeeCustomFieldHistory(employeeId, historyId),
		onSuccess: () => {
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory", employeeId] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields", employeeId] });
		},
		meta: {
			errorMessage: "There was a problem deleting the employee custom field history",
		},
	});
};

export const useDeleteEmployeeAvatar = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();

	return useMutation({
		mutationKey: ["employeeAvatarDelete"],
		mutationFn: async (employeeId: string) => {
			await deleteEmployeeAvatar(employeeId);
		},
		...options,
		onSuccess: (data, employeeId, context) => {
			options?.onSuccess && options.onSuccess(data, employeeId, context);
			setTimeout(() => {
				refetchCachedImages(employeeId);
				client.invalidateQueries({ queryKey: ["InternalApiUser", employeeId] });
			}, 1000);
		},
	});
};

export const useMutateEmployeeAvatar = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();

	return useMutation({
		mutationKey: ["employeeAvatarUpdate"],
		mutationFn: async ({ employeeId, file }: { employeeId: string; file: File }) => {
			const newFile = await cropImageToCenterRange(file, { width: 200, height: 200 });

			await postEmployeeAvatar(employeeId, newFile);
		},
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);

			setTimeout(() => {
				refetchCachedImages(variables?.employeeId);
				client.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
			}, 1000);
		},
	});
};

export const useMutateEmployeeActivation = (options: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["mutateEmployeeActivation"],
		mutationFn: async ({
			employeeId,
			activate,
			termination,
			managerReassignmentId,
		}: {
			employeeId: string;
			activate: boolean;
			termination?: Date;
			managerReassignmentId?: string;
		}) => {
			await editEmployeeActivation(employeeId, activate, termination, managerReassignmentId);
		},
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);

			client.invalidateQueries({ queryKey: ["employeeCustomFields", variables.employeeId] });
			client.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
		},
	});
};
